import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
import { IBasket } from "../../../models/catalogue";
import { IBasketLineAPIURL } from "../../../models/nominals";
import { reloadBasket } from "./reloadBasket";

/**
 * Arguments for removeBasketLine thunk.
 */
interface RemoveBasketLineArgs {
    lineURL: IBasketLineAPIURL;
}

/**
 * Remove a line from the basket.
 *
 * This thunk removes a product line from the user's basket and reloads
 * the basket to reflect updated totals and offers. Used in cart views
 * and checkout flows when removing items.
 *
 * @param {IBasketLineAPIURL} args.lineURL - API URL of the basket line to remove
 *
 * @example
 * // Remove a line from basket
 * dispatch(removeBasketLine({ lineURL: line.url }));
 *
 * @sideEffects
 * - Makes API call to remove line via basketAPI.removeLine
 * - Dispatches reloadBasket thunk to reload basket data
 * - Updates `data.basket` with reloaded basket via extraReducers
 * - May trigger offer recalculations on the backend
 */
export const removeBasketLine = createAsyncThunk(
    "basket/removeBasketLine",
    async (
        args: RemoveBasketLineArgs,
        { dispatch },
    ): Promise<IBasket | null> => {
        await basketAPI.removeLine(args.lineURL);
        const { basket } = await dispatch(reloadBasket()).unwrap();
        return basket;
    },
);
